import { useCallback, useEffect, useState } from 'react';
import { campaignService, getErrorMessage } from '../../../../services/apiService';
import { queryCache } from '../../../../services/queryCache';
import { fetchAllPages } from './fetchAllPages';

const PAGE = 200; // the audience endpoint's ceiling per page
/** Well above the base today (5.5k on the test box); if it is ever hit, the screens say so. */
const CAP = 20000;
/** Pages asked for side by side: about four times quicker than one after another, without crowding the API. */
const CONCURRENCY = 4;
const KEY = 'campaign:all-vendors';
/**
 * Any write clears the cache, so this only bounds how old an untouched view can get while someone
 * moves between the regularization screens.
 */
const MAX_AGE_MS = 5 * 60_000;

const loadAll = (onProgress) =>
  fetchAllPages(
    async (page) => {
      const res = await campaignService.audience({ page, limit: PAGE });
      return { items: res.data || [], totalPages: res.meta?.totalPages || 1, total: res.matched ?? res.total };
    },
    { concurrency: CONCURRENCY, cap: CAP, onProgress }
  );

const fromHeld = (all) => ({
  vendors: all.items,
  loading: false,
  error: null,
  loaded: all.items.length,
  total: all.total ?? all.items.length,
  truncated: all.truncated,
});

/**
 * The whole vendor base, once, for screens that pick vendors: filtering, searching, "select all"
 * and "who lacks what" then happen in the browser, instantly, instead of a round trip per
 * keystroke. Reports progress while the pages come in.
 *
 * Shared across screens: Regularization, the self-regularization hub, and both wizards read the
 * same copy, so moving between them does not download the base again. `reload()` always does.
 */
export function useAllVendors({ enabled = true } = {}) {
  const [state, setState] = useState(() => {
    const held = queryCache.peek(KEY);
    return held ? fromHeld(held.data) : { vendors: null, loading: enabled, error: null, loaded: 0, total: 0, truncated: false };
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const force = tick > 0;
    const held = queryCache.peek(KEY);
    if (held && !force) setState(fromHeld(held.data));
    else if (!held) setState((s) => ({ ...s, loading: true, error: null }));

    const onProgress = (loaded, total) => {
      if (!cancelled && !held) setState((s) => ({ ...s, loaded, total }));
    };
    queryCache
      .fetch(KEY, () => loadAll(onProgress), { maxAgeMs: MAX_AGE_MS, force })
      .then((all) => {
        if (!cancelled) setState(fromHeld(all));
      })
      .catch((err) => {
        if (!cancelled) setState((s) => ({ ...s, loading: false, error: getErrorMessage(err) || 'Could not load vendors' }));
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

export default useAllVendors;
